/**
 * Один капт: обе стороны с уроном и киллами.
 *
 * Состав противника есть только у боёв, записанных с 14 августа 2026 года.
 * До этого бот хранил лишь свою сторону, а игровая служба помнит последние
 * полсотни каптов сервера — задним числом чужие цифры взять неоткуда, и
 * притворяться, что их нет по другой причине, не стоит.
 */

import type { ReactNode } from 'react';

import { formatDateTime } from '../lib/format.ts';
import { navigate, useLinkHandler } from '../lib/router.ts';
import { useResource } from '../lib/useResource.ts';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  StatCard,
} from '../components/ui.tsx';
import { captureOutcome, type FamilySummary } from '../lib/stats.ts';

interface CapturePlayer {
  staticId: string;
  name: string;
  damage: number;
  kills: number;
  familyId: number;
  ours: boolean;
  userId: string | null;
  gameNick: string | null;
}

interface CaptureResponse {
  capture: {
    captureId: number;
    familyId: number;
    startedAt: string;
    gangZoneId: number | null;
    defendersId: number;
    attackersId: number;
    defendersName: string | null;
    attackersName: string | null;
    winner: number | null;
  };
  family: FamilySummary;
  players: CapturePlayer[];
}

const number = (value: number): string => value.toLocaleString('ru');

function RosterTable({ players, timezone }: { players: CapturePlayer[]; timezone: string }): ReactNode {
  const onLinkClick = useLinkHandler();
  void timezone;

  const damage = players.reduce((sum, player) => sum + player.damage, 0);
  const kills = players.reduce((sum, player) => sum + player.kills, 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] border-collapse text-sm">
        <thead>
          <tr style={{ background: 'var(--surface-raised)' }}>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>
              Игрок
            </th>
            <th className="px-4 py-2 text-right text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>
              Урон
            </th>
            <th className="px-4 py-2 text-right text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>
              Киллы
            </th>
          </tr>
        </thead>
        <tbody>
          {players.map((player, index) => (
            <tr key={player.staticId} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
              <td className="px-4 py-2">
                <span className="mr-2 tabular-nums" style={{ color: 'var(--text-muted)' }}>
                  {index + 1}.
                </span>
                {player.userId === null ? (
                  <span>{player.gameNick ?? player.name}</span>
                ) : (
                  <a
                    href={`/players/${player.userId}`}
                    onClick={onLinkClick}
                    className="underline decoration-dotted underline-offset-4"
                    style={{ color: 'var(--accent)' }}
                  >
                    {player.gameNick ?? player.name}
                  </a>
                )}
                <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {player.staticId}
                </span>
              </td>
              <td className="px-4 py-2 text-right tabular-nums">{number(player.damage)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{player.kills}</td>
            </tr>
          ))}
          <tr className="border-t" style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-raised)' }}>
            <td className="px-4 py-2 text-xs uppercase" style={{ color: 'var(--text-muted)' }}>
              итого · {players.length} чел.
            </td>
            <td className="px-4 py-2 text-right font-medium tabular-nums">{number(damage)}</td>
            <td className="px-4 py-2 text-right font-medium tabular-nums">{kills}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function CaptureDetailPage({ captureId, timezone }: { captureId: number; timezone: string }): ReactNode {
  const { data, loading, error, reload } = useResource<CaptureResponse>(
    `/api/stats/captures/${String(captureId)}`,
  );

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  if (loading || data === null) {
    return <Skeleton rows={8} />;
  }

  const { capture, players } = data;
  const ours = players.filter((player) => player.ours);
  const enemies = players.filter((player) => !player.ours);
  const outcome = captureOutcome(capture);
  const weDefended = capture.defendersId === capture.familyId;
  const enemyName =
    (weDefended ? capture.attackersName : capture.defendersName) ??
    `семья ${String(weDefended ? capture.attackersId : capture.defendersId)}`;
  const ourName = (weDefended ? capture.defendersName : capture.attackersName) ?? data.family.name ?? 'наша семья';

  return (
    <>
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            navigate('/stats');
          }}
        >
          ← К статистике
        </Button>
      </div>

      <PageHeader
        title={`Капт #${String(capture.captureId)}`}
        description={`${formatDateTime(capture.startedAt, timezone)} · ${ourName} против ${enemyName}${
          capture.gangZoneId === null ? '' : ` · зона ${String(capture.gangZoneId)}`
        }`}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Исход"
          value={outcome === 'win' ? 'победа' : outcome === 'loss' ? 'поражение' : 'неизвестно'}
          tone={outcome === 'win' ? 'positive' : outcome === 'loss' ? 'danger' : 'neutral'}
        />
        <StatCard label="Мы" value={weDefended ? 'защита' : 'атака'} />
        <StatCard label="Наш урон" value={number(ours.reduce((sum, player) => sum + player.damage, 0))} />
        <StatCard label="Наши киллы" value={ours.reduce((sum, player) => sum + player.kills, 0)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel className="overflow-hidden">
          <PanelHeader title={ourName} description={`Наш состав · ${String(ours.length)} чел.`} />
          {ours.length === 0 ? (
            <EmptyState title="Состав не записан" description="Служба не прислала состав нашей стороны." />
          ) : (
            <RosterTable players={ours} timezone={timezone} />
          )}
        </Panel>

        <Panel className="overflow-hidden">
          <PanelHeader title={enemyName} description={`Противник · ${String(enemies.length)} чел.`} />
          {enemies.length === 0 ? (
            <EmptyState
              title="Состав противника не сохранён"
              description="Бот начал записывать чужие составы 14 августа 2026 года. У боёв до этой даты их взять неоткуда: служба помнит только последние полсотни каптов."
            />
          ) : (
            <RosterTable players={enemies} timezone={timezone} />
          )}
        </Panel>
      </div>
    </>
  );
}
